import { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Typography } from "@material-ui/core";
import { formatHosDuration } from "app/main/utils/uiUtils";

const HOS_DRIVE_WARNING_SECONDS = 1800;

const USED_COLOR = "#546E7A";
const WARNING_COLOR = "#D32F2F";
const CAUTION_COLOR = "#F9A825";
const DRIVE_OK_COLOR = "#4CAF50";
const DWELL_COLOR = "#5C6BC0";

const getDriveRemainingColor = (remaining, warning) => {
	if (remaining == null || remaining < 0 || warning) return WARNING_COLOR;
	if (remaining < HOS_DRIVE_WARNING_SECONDS) return CAUTION_COLOR;
	return DRIVE_OK_COLOR;
};

function HosTimelineBar({ title, series, colors, max, legendItems }) {
	const options = useMemo(
		() => ({
			chart: {
				type: "bar",
				stacked: true,
				sparkline: { enabled: false },
				toolbar: { show: false },
				animations: { enabled: false },
			},
			plotOptions: {
				bar: {
					horizontal: true,
					barHeight: "55%",
					borderRadius: 2,
				},
			},
			colors,
			dataLabels: { enabled: false },
			xaxis: {
				max: max || undefined,
				labels: { show: false },
				axisBorder: { show: false },
				axisTicks: { show: false },
			},
			yaxis: { show: false },
			grid: { show: false, padding: { left: 0, right: 0, top: -16, bottom: -8 } },
			legend: { show: false },
			tooltip: { enabled: false },
		}),
		[colors, max]
	);

	return (
		<div className="w-full mb-12">
			<Typography className="text-11 font-semibold text-grey-800 mb-4" component="div">
				{title}
			</Typography>
			<div className="flex flex-col space-y-3 mb-4">
				{legendItems.map((item) => (
					<div key={item.label} className="flex flex-row items-center justify-between">
						<div className="flex flex-row items-center">
							<div className="w-8 h-8 rounded-sm mr-6 flex-shrink-0" style={{ backgroundColor: item.color }} />
							<Typography className="text-11 text-grey-800" component="span">
								{item.label}
							</Typography>
						</div>
						<Typography className="text-11 font-medium" component="span">
							{formatHosDuration(item.value)}
						</Typography>
					</div>
				))}
			</div>
			<ReactApexChart options={options} series={series} type="bar" height={28} width="100%" />
		</div>
	);
}

/**
 * Drive bar: drive to stop + drive left.
 * Duty bar: drive to stop + dwell (one segment) and duty left.
 */
function HosCombinedUsageBar({
	secondsToStop,
	driveRemaining,
	dutyRemaining,
	dutyRequiredAtStop = 3 * 3600,
	driveWarning = false,
	dutyWarning = false,
}) {
	const driveBar = useMemo(() => {
		const driveToStop = Math.max(0, secondsToStop ?? 0);
		const driveLeft = Math.max(0, driveRemaining ?? 0);
		const driveLeftColor = getDriveRemainingColor(driveRemaining, driveWarning);

		return {
			series: [
				{ name: "Drive to stop", data: [driveToStop] },
				{ name: "Drive left", data: [driveLeft] },
			],
			colors: [USED_COLOR, driveLeftColor],
			max: Math.max(driveToStop + driveLeft, 1),
			legendItems: [
				{ label: "Drive to stop", value: secondsToStop, color: USED_COLOR },
				{ label: "Drive left", value: driveRemaining, color: driveLeftColor },
			],
		};
	}, [secondsToStop, driveRemaining, driveWarning]);

	const dutyBar = useMemo(() => {
		const driveToStop = Math.max(0, secondsToStop ?? 0);
		const dwell = dutyRequiredAtStop;
		const toStopPlusDwell = driveToStop + dwell;
		const dutyLeft = (dutyRemaining ?? 0) - dwell;
		const positiveDutyLeft = Math.max(0, dutyLeft);
		const dutyLeftShortfall = dutyLeft < 0;

		const consumedColor = dutyWarning || (dutyRemaining ?? 0) < dwell ? WARNING_COLOR : DWELL_COLOR;
		const dutyLeftColor = dutyLeftShortfall || dutyWarning ? WARNING_COLOR : DRIVE_OK_COLOR;

		return {
			series: [
				{ name: "Drive to stop + Dwell", data: [toStopPlusDwell] },
				{ name: "Duty left", data: [positiveDutyLeft] },
			],
			colors: [consumedColor, dutyLeftColor],
			max: Math.max(toStopPlusDwell + positiveDutyLeft, toStopPlusDwell, 1),
			legendItems: [
				{ label: "Drive to stop + Dwell", value: toStopPlusDwell, color: consumedColor },
				{ label: "Duty left", value: dutyLeft, color: dutyLeftColor },
			],
		};
	}, [secondsToStop, dutyRemaining, dutyRequiredAtStop, dutyWarning]);

	if (secondsToStop == null && driveRemaining == null && dutyRemaining == null) return null;

	return (
		<div className="w-full">
			<HosTimelineBar title="Drive time" {...driveBar} />
			<HosTimelineBar title="Duty time" {...dutyBar} />
		</div>
	);
}

export default HosCombinedUsageBar;
